import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@personic/shared'

export interface TeamMember {
  id: string
  email: string
  name: string
  role: string
  is_active: boolean
  email_verified: boolean
  created_at: string
  last_login: string | null
}

export interface Invitation {
  id: string
  tenant_id: string
  email: string
  role: string
  status: string
  expires_at: string | null
  accepted_at: string | null
  created_at: string
  invited_by_id: string | null
}

interface InviteRequest {
  email: string
  role: string
}

interface RoleUpdateRequest {
  role: string
}

export function useTeamMembers(tenantId: string) {
  return useQuery<TeamMember[]>({
    queryKey: ['team-members', tenantId],
    queryFn: async () => {
      const res = await api.get(`/tenants/${tenantId}/users`)
      return res.data
    },
    enabled: !!tenantId,
  })
}

export function useInvitations(tenantId: string) {
  return useQuery<Invitation[]>({
    queryKey: ['invitations', tenantId],
    queryFn: async () => {
      const res = await api.get(`/tenants/${tenantId}/invitations`)
      return res.data
    },
    enabled: !!tenantId,
  })
}

export function useInviteTeamMember(tenantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: InviteRequest) => {
      const res = await api.post(`/tenants/${tenantId}/invitations`, data)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitations', tenantId] })
    },
  })
}

export function useRemoveTeamMember(tenantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (userId: string) => {
      await api.delete(`/tenants/${tenantId}/users/${userId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-members', tenantId] })
    },
  })
}

export function useUpdateMemberRole(tenantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: string }) => {
      const res = await api.put(`/tenants/${tenantId}/users/${userId}/role`, {
        role,
      } as RoleUpdateRequest)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-members', tenantId] })
    },
  })
}

export function useCancelInvitation(tenantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (invitationId: string) => {
      await api.delete(`/tenants/${tenantId}/invitations/${invitationId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitations', tenantId] })
    },
  })
}
